import { isSupabaseConfigured } from "@/lib/supabase"
import { getClerkSessionInfo } from './clerkSession'
import { captureError } from '@/lib/sentry'
import { NetworkError } from '@/types/errors'
import type {
  BridgeAction,
  BridgePayload,
  BridgeResult,
  OptionalBridgeAction
} from '../contracts/apiBridge'

// Default timeout for API requests (30 seconds)
const DEFAULT_TIMEOUT_MS = 30000

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const functionsBaseUrl = supabaseUrl
  ? supabaseUrl.replace('https://', 'https://').replace('.supabase.co', '.functions.supabase.co')
  : ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

interface CallBridgeOptions {
  requireAuth?: boolean
  envUrl?: string
  envAnonKey?: string
}

export async function callBridge<A extends OptionalBridgeAction>(
  action: A,
  payload?: BridgePayload<A>,
  options?: CallBridgeOptions
): Promise<BridgeResult<A>>
export async function callBridge<A extends Exclude<BridgeAction, OptionalBridgeAction>>(
  action: A,
  payload: BridgePayload<A>,
  options?: CallBridgeOptions
): Promise<BridgeResult<A>>
export async function callBridge(
  action: BridgeAction,
  payload?: BridgePayload<BridgeAction>,
  options: CallBridgeOptions = {}
): Promise<BridgeResult<BridgeAction>> {
  const { requireAuth = true, envUrl, envAnonKey } = options

  // Use environment-specific URL and key if provided, otherwise fall back to default
  const targetUrl = envUrl || supabaseUrl
  const targetAnonKey = envAnonKey || supabaseAnonKey
  const targetFunctionsUrl = envUrl
    ? envUrl.replace('https://', 'https://').replace('.supabase.co', '.functions.supabase.co')
    : functionsBaseUrl

  if (!targetFunctionsUrl || !isSupabaseConfigured()) {
    throw new Error('Supabase functions URL not configured')
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  }

  if (targetAnonKey) {
    headers['apikey'] = targetAnonKey
  }

  const bridgePayload: Record<string, unknown> = payload && typeof payload === 'object'
    ? { ...(payload as Record<string, unknown>) }
    : {}

  if (requireAuth) {
    const { token, userId, organizationId, email } = await getClerkSessionInfo()
    headers['Authorization'] = `Bearer ${targetAnonKey}`
    headers['X-Clerk-Token'] = token
    if (userId && bridgePayload.clerkUserId === undefined) bridgePayload.clerkUserId = userId
    if (organizationId && bridgePayload.organizationId === undefined) bridgePayload.organizationId = organizationId
    if (email && bridgePayload.clerkUserEmail === undefined) bridgePayload.clerkUserEmail = email
  } else if (targetAnonKey) {
    headers['Authorization'] = `Bearer ${targetAnonKey}`
  }

  // Create AbortController for timeout handling
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS)

  let response: Response
  let rawText: string

  try {
    response = await fetch(`${targetFunctionsUrl}/clerk-bridge`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ action, payload: bridgePayload }),
      signal: controller.signal
    })
    rawText = await response.text()
  } catch (fetchError) {
    clearTimeout(timeoutId)
    // Handle network errors (connection refused, timeout, etc.)
    if (fetchError instanceof Error) {
      const isTimeout = fetchError.name === 'AbortError'
      const networkError = new NetworkError(
        isTimeout
          ? `Request timed out after ${DEFAULT_TIMEOUT_MS / 1000}s`
          : `Network error: ${fetchError.message}`,
        isTimeout
      )
      captureError(networkError, {
        tags: { action, error_type: 'network' },
        extra: { original_error: fetchError.message }
      })
      throw networkError
    }
    throw fetchError
  } finally {
    clearTimeout(timeoutId)
  }

  let data: any = {}
  if (rawText) {
    try {
      data = JSON.parse(rawText)
    } catch {
      data = { raw: rawText }
    }
  }

  if (!response.ok || data?.error) {
    const message = data?.error || rawText || response.statusText

    // Log to console for development
    console.error('Bridge request failed', {
      action,
      payload: bridgePayload,
      status: response.status,
      response: data
    })

    // Capture to Sentry with context (scrub sensitive data)
    const safePayload = bridgePayload
      ? { ...bridgePayload, clerkToken: undefined, token: undefined }
      : undefined
    captureError(new Error(message), {
      tags: {
        action,
        status_code: String(response.status),
      },
      extra: {
        payload: safePayload,
        response_error: data?.error,
      },
    })

    throw new Error(message)
  }

  return data?.result as BridgeResult<typeof action>
}
